import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFencedState as useState } from '../../lib/bowlMutationContext.jsx'
import { api } from '../../lib/api.js'
import styles from './BowlCommitteeWork.module.css'

/**
 * BOWL COMMITTEE ITEMS ON THE WORKLIST.
 *
 * Brian, 2026-10-26: "everything must be FULLY integrated into the rep
 * worklist." The committee press is six steps per bowl plus the gaps that
 * block it; the server derives them (GET /api/bowl-committees/worklist) and
 * this renders the caller's open items, soonest bowl first, with the action,
 * the reason and the due date. An item clears when its step is recorded done
 * on the bowl card, which is where every one of these links.
 *
 * `person` is the name the worklist is showing. Empty = the signed-in person.
 * An exec may open anyone's; the server refuses that to a rep.
 */
export default function BowlCommitteeWork({ person = '' }) {
  const [st, setSt] = useState({ status: 'loading', data: null, error: '' })

  useEffect(() => {
    const ctl = new AbortController()
    setSt({ scope: person, status: 'loading', data: null, error: '' })
    api.bowlCommitteeWorklist(person || undefined, {signal: ctl.signal})
      .then((d) => { if (!ctl.signal.aborted) setSt({ scope: person, status: 'ready', data: d, error: '' }) })
      .catch((e) => { if (!ctl.signal.aborted) setSt({ scope: person, status: 'error', data: null, error: e?.body?.detail || e?.message || 'Could not read the bowl committee items.' }) })
    return () => ctl.abort()
  }, [person, setSt])

  if (st.scope !== person || st.status === 'loading') return <p className={styles.muted}>Reading bowl committee items…</p>
  if (st.status === 'error') {
    return <p className={styles.bad}>Bowl committee items could not be read, so none are shown here. This is not an empty list. {st.error}</p>
  }
  const items = st.data?.items || []
  const next = items.filter((i) => i.state === 'next')
  const queued = items.length - next.length

  return (
    <section className={styles.wrap} aria-label="Bowl committee items">
      <h2 className={styles.h}>Bowl committees · {next.length} to do now</h2>
      <p className={styles.muted}>
        {next.length === 0
          ? (st.data?.note || 'Nothing to do on bowl committees right now.')
          : `${queued} more queued behind these. Soonest bowl first; each clears when its step is recorded done.`}
      </p>
      <ul className={styles.list}>
        {next.map((i) => (
          <li key={i.id} className={styles.item}>
            <span className={styles.top}>
              <b>{i.bowlName}</b>
              <span className={styles.when}>{i.date || 'date not confirmed'}{i.daysToGame != null ? ` · ${i.daysToGame} days` : ''} · {i.priority}</span>
            </span>
            <span className={i.kind === 'gap' ? styles.gap : styles.label}>{i.label}</span>
            <span>{i.action}</span>
            <span className={styles.why}>{i.reason} Due {i.due}.</span>
            <Link className={styles.open} to={`/bowl-eligibility?view=committees&bowl=${encodeURIComponent(i.bowl)}`}>Open the bowl</Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
